import { useCallback } from "react";

import { ClientResponse } from "@/Common/Api/ClientResponse";

/**
 * A function that performs an API request and returns a typed ClientResponse.
 * @template T - The type of the response data.
 */
type ApiFn<T = any> = () => Promise<ClientResponse<T>>;

/**
 * Options passed to `callApi`.
 * @template T - The expected data type returned by the API.
 * @property onSuccess - Optional callback invoked with the parsed `data` on success.
 * @property onError - Optional callback invoked with error details when the API returns errors.
 */
type CallApiOptions<T = any> = {
    onSuccess?: (data?: T) => void;
    onError?: (errors: any) => void;
};

/**
 * Hook that provides a small helper for calling APIs and handling common success/error callbacks.
 *
 * Usage:
 * const { callApi } = useApi();
 * await callApi(() => myApiClient.doThing(), { onSuccess: data => ..., onError: errors => ... });
 *
 * The hook is intentionally thin — it defers UI handling to the caller via callbacks.
 */
export default function useApi(): { callApi: <T = any>(apiFn: ApiFn<T>, options?: CallApiOptions<T>) => Promise<ClientResponse<T>> } {
    const callApi = useCallback(async <T>(apiFn: ApiFn<T>, options?: CallApiOptions<T>): Promise<ClientResponse<T>> => {
        const response = await apiFn();

        if (!response.isSuccess) {
            if (options?.onError) options.onError(response.errors);
            return response;
        }

        if (options?.onSuccess) options.onSuccess(response.data);
        return response;
    }, []);

    return { callApi };
}
